import { noop } from 'lodash';
import React, { PureComponent } from 'react';
import styled, { withTheme } from 'styled-components';
import {
    Close,
    Hamburger,
    MagnifyingGlass,
} from '@zocdoc-frontend-common/assets-icons';
import CollapsibleSection, {
    CollapsibleSectionPrimitives,
} from '@zocdoc-frontend-common/components-collapsiblesection';
import { EmailFirstSignInFormView } from '@zocdoc-frontend-common/components-email-first-sign-in-form';
import { LinkFarmToggle } from '@zocdoc-frontend-common/components-footer/LinkFarmToggle';
import Sitemap from '@zocdoc-frontend-common/components-footer/Sitemap';
import { Link as ZdLink } from '@zocdoc-frontend-common/components-link';
import {
    SpecialtyLinks,
    InsuranceLinks,
} from '@zocdoc-frontend-common/components-link-farm/LinkConstants';
import { MFAVerificationForm } from '@zocdoc-frontend-common/components-mfa-verification-form';
import PoweredByZocdoc from '@zocdoc-frontend-common/components-powered-by-zocdoc-badge';
import { USER_TYPE } from '@zocdoc-frontend-common/state-user-account/constants';
import { breakpoint, Breakpoints } from '@zocdoc-frontend-common/styles-grid';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { DropdownLinks } from '@zocdoc-frontend-common/utils-constants/HeaderLinks';
import { filterToValidAttributes } from '@zocdoc-frontend-common/utils-dom-property';
import {
    Metrics,
    MetricsClickable,
    CustomMetrics,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import {
    lazyImport,
    LazyComponent,
} from '@zocdoc-frontend-common/utils-lazyimportcomponent';
import { Portal } from '@zocdoc-frontend-common/utils-portal';
import ScrollLock from '@zocdoc-frontend-common/utils-scrolllock';
import { green, grey25, navy, white, yellow } from '@zocdoc/branding/colors';
import {
    GA_ACTION_TEXT,
    TRACKING_ELEMENTS,
    MobileUserLinks,
} from './constants';
import { SpecialtyAccordion } from './TopListingPages/SpecialtyAccordion';

const getLoginForm = lazyImport(
    () =>
        import(
            /* webpackChunkName: "loginform", webpackPrefetch: 40 */ '@zocdoc-frontend-common/components-log-in-form'
        ).then((module) => ({ default: module.LoginFormWithMetric })),
    'LoginForm'
);

/**
 * @param {string} appleLoginUrl
 * @param {JSX.Element} children
 * @param {string} facebookLoginUrl
 * @param {Object} fakeImportLoginForm
 * @param {boolean} forceOpen
 * @param {boolean} forceOpenCollapsibleLogin
 * @param {string} googleLoginUrl
 * @param {string} hostOverride
 * @param {boolean} isInEmailFirstLoginAndSignup
 * @param {boolean} isMfaEnabled
 * @param {boolean} isUserLocked
 * @param {string} loginReturnUrl
 * @param {string} mobileMenuTriggerDataTest
 * @param {function(): void} onBeforeLinkClick
 * @param {function(): void} recordRememberMeAssignment
 * @param {function(): void} resetLoginForm
 * @param {Functions_Shared_Fem_SendMetric} sendMetric
 * @param {boolean} showLoginRememberMe
 * @param {boolean} splitLoginAndSignupLink
 * @param {string} suggestedRedirect
 * @param {{
 *     header: {
 *         logo: {
 *             linkUrl: string,
 *         },
 *         mobileMenu: {
 *             links: {
 *                 text: string,
 *                 title: string,
 *                 uri: string,
 *             }[],
 *             signIn: {
 *                 enabled: boolean,
 *             },
 *         },
 *         showPoweredByZocdoc: boolean,
 *     },
 * }} theme
 * @param {{
 *     cities: {
 *         name: string,
 *         url: string,
 *     }[],
 *     specialties: {
 *         name: string,
 *         url: string,
 *     }[],
 * }} topListingPages
 * @param {boolean} useCollapsibleLogin
 * @param {boolean} usePatientHomeView
 * @param {Object} user
 * @param {boolean} __forceOpenListingPages
 * @returns {JSX.Element}
 */
class MobileMenu extends PureComponent {
    static defaultProps = {
        isInEmailFirstLoginAndSignup: false,
        isSiteFooterContactLinkGroupEnabled: false,
        isMfaEnabled: false,
        isUserLocked: false,
        facebookLoginUrl: '',
        forceOpen: false,
        forceOpenCollapsibleLogin: false,
        googleLoginUrl: '',
        appleLoginUrl: '',
        hostOverride: '',
        resetLoginForm: noop,
        loginReturnUrl: '',
        mobileMenuTriggerDataTest: 'mobile-menu-trigger',
        sendMetric: noop,
        showLoginRememberMe: false,
        splitLoginAndSignupLink: false,
        suggestedRedirect: '',
        useCollapsibleLogin: false,
        usePatientHomeView: false,
    };

    constructor(props) {
        super(props);

        this.state = {
            open: props.forceOpen,
            viewSitemap: false,
            viewMoreSpecialties: false,
            viewMoreInsurances: false,
            showTopSpecialties: {},
        };
    }

    componentDidMount() {
        window.addEventListener('resize', this.handleWindowResize, true);
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            isUserLocked,
            sendMetric,
            splitLoginAndSignupLink,
            suggestedRedirect,
        } = this.props;

        if (isUserLocked && prevProps.isUserLocked !== isUserLocked) {
            window.location.assign(suggestedRedirect);
        }

        if (!prevState.open && this.state.open && splitLoginAndSignupLink) {
            sendMetric({
                action: GA_ACTION_TEXT.showPatientSplitLoginAndSignup,
                element: TRACKING_ELEMENTS.patientLogin,
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
            });
        } else if (!prevState.open && this.state.open) {
            sendMetric({
                action: GA_ACTION_TEXT.showPatientDefaultLoginAndSignup,
                element: TRACKING_ELEMENTS.patientLogin,
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
            });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleWindowResize, true);
    }

    render() {
        return (
            <div {...filterToValidAttributes(this.props)}>
                <Metrics action="Click Mobile Menu Link" section="BurgerMenu">
                    {this.maybeRenderTrigger()}
                    {this.maybeRenderMenu()}
                </Metrics>
                {this.state.open && <ScrollLock />}
            </div>
        );
    }

    maybeRenderTrigger() {
        const { mobileMenuTriggerDataTest, theme } = this.props;
        if (theme.header.mobileMenu.enabled) {
            return (
                <MenuTriggerArea>
                    <MetricsClickable component="Trigger" element="Trigger">
                        <MenuTrigger
                            data-test={mobileMenuTriggerDataTest}
                            onClick={this.toggleMenu}
                            title="Open the mobile navigation menu"
                        />
                        <div>
                            <Hamburger color="currentColor" height={11.531} />
                        </div>
                    </MetricsClickable>
                </MenuTriggerArea>
            );
        }
    }

    maybeRenderMenu() {
        const { theme } = this.props;
        const isWhiteLabel = theme.directoryId !== '-1';

        if (this.state.open) {
            return (
                <Portal inline={this.props.forceOpen} portalId="zd_mobile_menu">
                    <Menu data-test="mobile-menu">
                        <Home isWhiteLabel={isWhiteLabel}>
                            <StyledCloseContainer
                                data-test="mobile-menu-close"
                                onClick={this.toggleMenu}
                            >
                                <StyledClose color="currentColor" />
                            </StyledCloseContainer>
                            {isWhiteLabel && (
                                <React.Fragment>
                                    <MagnifyingGlass
                                        color="currentColor"
                                        height={20}
                                    />
                                    <HomeLink
                                        data-test="mobile-menu-home-link"
                                        href={
                                            this.props.theme.header.logo.linkUri
                                        }
                                    >
                                        Find a doctor or dentist
                                    </HomeLink>
                                </React.Fragment>
                            )}
                        </Home>

                        {this.maybeRenderUserSection()}
                        {this.props.children}
                        {this.maybeRenderCustomLinks()}
                        {this.renderSecondaryContent()}
                    </Menu>
                </Portal>
            );
        }
    }

    getLinks() {
        const { usePatientHomeView, user } = this.props;

        const mobileUserLinks = MobileUserLinks(usePatientHomeView);
        switch (user.user_type) {
            case USER_TYPE.Patient:
                return mobileUserLinks.Patient;
            case USER_TYPE.Provider:
                return mobileUserLinks.Provider;
            case USER_TYPE.ZdEmployee:
                return mobileUserLinks.ZdEmployee;
            default:
                throw Error(
                    `Unexpected user type ${user.user_type} in generating mobile menu links`
                );
        }
    }

    maybeRenderUserSection() {
        if (this.props.theme.header.mobileMenu.signIn.enabled) {
            if (this.props.signedIn) {
                const links = this.getLinks();
                return this.renderUserLinks(links);
            } else {
                return this.renderSignIn();
            }
        }
    }

    renderUserLinks(links) {
        return (
            <UserSection>
                <UserLinkContainer data-test="mobile-menu-user-links">
                    {links.map((link) => {
                        return this.renderUserLink(link);
                    })}
                </UserLinkContainer>
            </UserSection>
        );
    }

    renderUserLink(link) {
        const linkIcon = link.icon ? (
            <UserLinkIcon>
                <link.icon height={link.iconHeight || 20} />
            </UserLinkIcon>
        ) : null;

        const trackingMessage = link.message.replace(/ /g, '');
        return (
            <MetricsClickable
                key={link.dataTest}
                component="UserLink"
                element={trackingMessage}
            >
                <UserLink
                    data-element-name={trackingMessage}
                    data-test={`mobile-menu-${link.dataTest}`}
                    href={`${this.props.hostOverride}${link.url}`}
                    onClick={this.trackUserLinks}
                >
                    {linkIcon}
                    <UserLinkText>{link.message}</UserLinkText>
                </UserLink>
            </MetricsClickable>
        );
    }

    renderSignIn() {
        const { splitLoginAndSignupLink } = this.props;
        if (splitLoginAndSignupLink) {
            return this.renderStackedSignIn();
        }
        return (
            <SignIn data-test="mobile-menu-signin-links">
                <Metrics component="SignInLinks">
                    <CustomMetrics>
                        {({ sendMetric }) => (
                            <SignInLink
                                data-element-name="mobile-menu-patient-login"
                                data-test="mobile-menu-patient-login"
                                href={`${this.props.hostOverride}${DropdownLinks.patientSignin}`}
                                onClick={(ev) => {
                                    sendMetric({
                                        element: TRACKING_ELEMENTS.patientLogin,
                                        initiator: EVENT_INITIATORS.USER,
                                        interactionType:
                                            INTERACTION_TYPES.CLICK,
                                    });
                                    this.trackSignInLinks(ev);
                                }}
                            >
                                <UserType>{`Patients`}</UserType>
                                <AccountText>{`Log in / Sign up`}</AccountText>
                            </SignInLink>
                        )}
                    </CustomMetrics>

                    <Divider />

                    <CustomMetrics>
                        {({ sendMetric }) => (
                            <SignInLink
                                data-element-name="mobile-menu-provider-login"
                                data-test="mobile-menu-provider-login"
                                href={`${this.props.hostOverride}${DropdownLinks.providerSignin}`}
                                onClick={(ev) => {
                                    sendMetric({
                                        element:
                                            TRACKING_ELEMENTS.providerLogin,
                                        initiator: EVENT_INITIATORS.USER,
                                        interactionType:
                                            INTERACTION_TYPES.CLICK,
                                    });
                                    this.trackSignInLinks(ev);
                                }}
                            >
                                <UserType>{`Doctors`}</UserType>
                                <AccountText>{`Log in`}</AccountText>
                            </SignInLink>
                        )}
                    </CustomMetrics>
                </Metrics>
            </SignIn>
        );
    }

    renderStackedSignIn() {
        return (
            <StackedSignIn>
                <CustomMetrics>
                    {({ sendMetric }) => (
                        <ZdLink
                            data-test="mobile-menu-patient-signup-link"
                            href={`${this.props.hostOverride}${DropdownLinks.patientSignin}`}
                            onClick={(ev) => {
                                sendMetric({
                                    action: GA_ACTION_TEXT.clickPatientSignupLink,
                                    element: TRACKING_ELEMENTS.patientSignup,
                                    initiator: EVENT_INITIATORS.USER,
                                    interactionType: INTERACTION_TYPES.CLICK,
                                });
                                this.trackSignInLinks(ev);
                            }}
                        >
                            <SignInText>{`Sign up`}</SignInText>
                        </ZdLink>
                    )}
                </CustomMetrics>
                {this.renderPatientLogin()}
            </StackedSignIn>
        );
    }

    renderSignInForm() {
        const {
            isMfaEnabled,
            fakeImportLoginForm,
            facebookLoginUrl,
            googleLoginUrl,
            appleLoginUrl,
            loginReturnUrl: returnUrl,
            recordRememberMeAssignment,
            showLoginRememberMe: showRememberMe,
            isInEmailFirstLoginAndSignup,
        } = this.props;

        const LoginForm = fakeImportLoginForm || getLoginForm();

        if (isMfaEnabled) {
            return (
                <StyledMfaWrapper>
                    <MFAVerificationForm
                        data-test="mobile-menu-mfa-verification-form"
                        isInMobileMenu={true}
                    />
                </StyledMfaWrapper>
            );
        }

        if (isInEmailFirstLoginAndSignup) {
            return (
                <StyledEmailFirstSignInFormView
                    {...this.props}
                    isInMobileMenu={true}
                    data-test={'mobile-menu-email-first-login-form'}
                />
            );
        }

        return (
            <LoginForm
                data-test="mobile-menu-login-form"
                facebookLoginUrl={facebookLoginUrl}
                googleLoginUrl={googleLoginUrl}
                appleLoginUrl={appleLoginUrl}
                recordRememberMeAssignment={recordRememberMeAssignment}
                returnUrl={returnUrl}
                showRememberMe={showRememberMe}
                showSignUpLink
                showProviderLoginLink
            />
        );
    }

    renderPatientLogin() {
        const {
            forceOpenCollapsibleLogin,
            resetLoginForm,
            useCollapsibleLogin,
        } = this.props;

        if (useCollapsibleLogin) {
            return (
                <CustomMetrics>
                    {({ sendMetric }) => (
                        <CollapsibleLogin
                            headerDataTest="mobile-menu-patient-collapsible-login"
                            headerOpenText={'Log in'}
                            headerText={'Log in'}
                            defaultOpen={forceOpenCollapsibleLogin}
                            onClick={({ isOpen }) => {
                                if (isOpen) {
                                    resetLoginForm();
                                    sendMetric({
                                        action: GA_ACTION_TEXT.clickOpenCollapsibleLogin,
                                        element:
                                            TRACKING_ELEMENTS.patientCollapsibleLogin,
                                        initiator: EVENT_INITIATORS.USER,
                                        interactionType:
                                            INTERACTION_TYPES.CLICK,
                                    });
                                } else if (isOpen === false) {
                                    sendMetric({
                                        action: GA_ACTION_TEXT.clickCloseCollapsibleLogin,
                                        element:
                                            TRACKING_ELEMENTS.patientCollapsibleLogin,
                                        initiator: EVENT_INITIATORS.USER,
                                        interactionType:
                                            INTERACTION_TYPES.CLICK,
                                    });
                                }
                            }}
                        >
                            <LazyComponent componentName="LoginForm">
                                {this.renderSignInForm()}
                            </LazyComponent>
                        </CollapsibleLogin>
                    )}
                </CustomMetrics>
            );
        } else {
            return (
                <CustomMetrics>
                    {({ sendMetric }) => (
                        <ZdLink
                            data-test="mobile-menu-patient-login-link"
                            href={`${this.props.hostOverride}${DropdownLinks.patientSignin}`}
                            onClick={(ev) => {
                                sendMetric({
                                    action: GA_ACTION_TEXT.clickPatientLoginLink,
                                    element: TRACKING_ELEMENTS.patientLogin,
                                    initiator: EVENT_INITIATORS.USER,
                                    interactionType: INTERACTION_TYPES.CLICK,
                                });
                                this.trackSignInLinks(ev);
                            }}
                        >
                            <SignInText isGreen>{`Log in`}</SignInText>
                        </ZdLink>
                    )}
                </CustomMetrics>
            );
        }
    }

    maybeRenderCustomLinks() {
        const { links } = this.props.theme.header.mobileMenu;

        if (links && links.length) {
            return (
                <CustomLinkSection data-test="mobile-menu-custom-links">
                    {links.map((definition, index) => (
                        <Link
                            key={index}
                            href={definition.uri}
                            title={definition.title}
                        >
                            {definition.text}
                        </Link>
                    ))}
                </CustomLinkSection>
            );
        }
    }

    renderFooterLink(url, element, component, text, isVisible = true) {
        if (!text) {
            text = element;
        }

        return (
            <MetricsClickable
                key={`mobile-link-${url}`}
                component={component}
                element={element}
            >
                <FooterLink
                    data-filter-component={component}
                    data-filter-name={element}
                    data-test={`mobile-link-${element}`}
                    href={url}
                    onClick={this.trackGenericMobileLinks}
                    isVisible={isVisible}
                >
                    {text}
                </FooterLink>
            </MetricsClickable>
        );
    }

    trackSignInLinks = async (ev) => {
        const element = ev.currentTarget.getAttribute('data-element-name');
        await this.props.onBeforeLinkClick(element, 'SignInLinks');
    };

    trackGenericMobileLinks = async (ev) => {
        const element = ev.currentTarget.getAttribute('data-filter-name');
        const component = ev.currentTarget.getAttribute(
            'data-filter-component'
        );
        await this.props.onBeforeLinkClick(element, component);
    };

    trackSocialMediaLinks = async (ev) => {
        const element = ev.currentTarget.getAttribute('data-element-name');
        await this.props.onBeforeLinkClick(element, 'SocialMediaLinks');
    };

    trackBadgeLinks = async (ev) => {
        const element = ev.currentTarget.getAttribute('data-link-name');
        await this.props.onBeforeLinkClick(element, 'AppDownload');
    };

    trackSeoLinks = async (ev) => {
        // Proceed GA Tracking
        const eventTarget = ev.currentTarget;
        const eventTargetText = eventTarget.textContent;
        const element = eventTargetText.replace(/\s/g, '');
        const component = `${eventTarget.getAttribute('data-title')}Links`;
        const { onBeforeLinkClick } = this.props;
        if (onBeforeLinkClick) {
            await onBeforeLinkClick(element, component);
        }
    };

    trackUserLinks = async (ev) => {
        const element = ev.currentTarget.getAttribute('data-element-name');
        await this.props.onBeforeLinkClick(element, element);
    };

    maybeRenderTopListingPagesLinks() {
        const { topListingPages, hostOverride } = this.props;
        if (
            this.props.signedIn ||
            !topListingPages ||
            !topListingPages.specialties ||
            !topListingPages.cities
        ) {
            return;
        }
        /* We need to bind because it employs other methods defined on MobileMenu object */
        const renderLink = this.renderFooterLink.bind(this);
        return (
            <SpecialtyAccordion
                topListingPages={topListingPages}
                renderFooterLink={renderLink}
                __forceOpenListingPages={this.props.__forceOpenListingPages}
                hostOverride={hostOverride}
            />
        );
    }

    renderSecondaryContent() {
        if (this.props.theme.header.showPoweredByZocdoc) {
            return (
                <Section data-test="mobile-menu-powered_by_zocdoc">
                    <StyledPoweredByZocdoc />
                </Section>
            );
        }

        const hostOverride = this.props.hostOverride;

        return (
            <Footer>
                <Section data-test="mobile-menu-zocdoc-links">
                    <SectionHeader>Zocdoc</SectionHeader>
                    {this.renderFooterLink(
                        `${hostOverride}/`,
                        'Home',
                        'ZocdocLinks',
                        'Home'
                    )}
                    {this.renderFooterLink(
                        `${hostOverride}/about`,
                        'About',
                        'ZocdocLinks',
                        'About us'
                    )}
                    {this.renderFooterLink(
                        `${hostOverride}/about/press`,
                        'Press',
                        'ZocdocLinks',
                        'Press'
                    )}
                    {this.renderFooterLink(
                        `${hostOverride}/careers`,
                        'Careers',
                        'ZocdocLinks',
                        'Careers'
                    )}
                    {this.props.isSiteFooterContactLinkGroupEnabled
                        ? null
                        : this.renderFooterLink(
                              `${hostOverride}/contactus`,
                              'Contact',
                              'ZocdocLinks',
                              'Contact us'
                          )}
                    {this.renderFooterLink(
                        '//support.zocdoc.com/patients/s',
                        'Help',
                        'ZocdocLinks',
                        'Help'
                    )}
                </Section>

                {this.props.isSiteFooterContactLinkGroupEnabled ? (
                    <Section data-test="mobile-menu-contact-links">
                        <SectionHeader>Contact</SectionHeader>
                        {this.renderFooterLink(
                            `mailto:service@zocdoc.com`,
                            'Email',
                            'ContactLinks',
                            'service@zocdoc.com'
                        )}
                        {this.renderFooterLink(
                            'tel:+18559623621',
                            'Phone',
                            'ContactLinks',
                            '1-855-962-3621'
                        )}
                    </Section>
                ) : null}

                {this.maybeRenderTopListingPagesLinks()}
                <Section data-test="mobile-menu-cta-links">
                    <SectionHeader>
                        Are you a top doctor or health service?
                    </SectionHeader>
                    {this.renderFooterLink(
                        `${hostOverride}/about/join?utm_medium=organicpro&utm_source=website&utm_campaign=PatientSite_footer`,
                        'ListYourPracticeOnZocdoc',
                        'AreYouADoctorLinks',
                        'List your practice on Zocdoc'
                    )}
                    {this.renderFooterLink(
                        `${hostOverride}/about/healthsystems`,
                        'LearnAboutZocdocForHealthSystems',
                        'AreYouADoctorLinks',
                        'Learn about Zocdoc for Health Systems'
                    )}
                    {this.renderFooterLink(
                        `${hostOverride}/about/integrate/`,
                        'BecomeAnEHRPartner',
                        'AreYouADoctorLinks',
                        'Become an EHR partner'
                    )}
                    )}
                </Section>

                <Section data-test="mobile-menu-discover-links">
                    <SectionHeader>Discover</SectionHeader>
                    {this.renderFooterLink(
                        'https://thepapergown.zocdoc.com/',
                        'ThePaperGown',
                        'DiscoverLinks',
                        'The Paper Gown: Stories for and about patients'
                    )}
                    {this.renderFooterLink(
                        'https://thescript.zocdoc.com/',
                        'TheScript',
                        'DiscoverLinks',
                        'The Script: Insights for doctors'
                    )}
                    {this.renderFooterLink(
                        `${hostOverride}/about/communitystandards/`,
                        'CommunityStandards',
                        'DiscoverLinks',
                        'Community Standards'
                    )}
                    {this.renderFooterLink(
                        `${hostOverride}/about/topic/data-privacy/`,
                        'DataAndPrivacy',
                        'DiscoverLinks',
                        'Data and privacy'
                    )}
                    {this.renderFooterLink(
                        `${hostOverride}/about/verifiedreviews/`,
                        'VerifiedReviews',
                        'DiscoverLinks',
                        'Verified reviews'
                    )}
                </Section>

                <Section data-test="mobile-menu-specialties-links">
                    <SectionHeader>Top specialties</SectionHeader>
                    {SpecialtyLinks.slice(0, 5).map((link) =>
                        this.renderFooterLink(
                            `${hostOverride}/${link.href}`,
                            link.text.replace(' ', ''),
                            'SpecialtyLinks',
                            link.text
                        )
                    )}
                    {this.state.viewMoreSpecialties || (
                        <LinkFarmToggle
                            toggleText="More"
                            toggleState={this.state.viewMoreSpecialties}
                            onToggleClick={
                                this.handleMoreSpecialtiesToggleClick
                            }
                        />
                    )}
                    {SpecialtyLinks.slice(5, SpecialtyLinks.length).map(
                        (link) =>
                            this.renderFooterLink(
                                `${hostOverride}/${link.href}`,
                                link.text.replace(' ', ''),
                                'SpecialtyLinks',
                                link.text,
                                this.state.viewMoreSpecialties
                            )
                    )}
                </Section>

                <Section data-test="mobile-menu-insurance-links">
                    <SectionHeader>Popular insurances</SectionHeader>
                    {InsuranceLinks.slice(0, 5).map((link) =>
                        this.renderFooterLink(
                            `${hostOverride}/${link.href}`,
                            link.text.replace(' ', ''),
                            'InsuranceLinks',
                            link.text
                        )
                    )}
                    {this.state.viewMoreInsurances || (
                        <LinkFarmToggle
                            toggleText="More"
                            toggleState={this.state.viewMoreInsurances}
                            onToggleClick={this.handleMoreInsurancesToggleClick}
                        />
                    )}
                    {InsuranceLinks.slice(5, InsuranceLinks.length).map(
                        (link) =>
                            this.renderFooterLink(
                                `${hostOverride}/${link.href}`,
                                link.text.replace(' ', ''),
                                'InsuranceLinks',
                                link.text,
                                this.state.viewMoreInsurances
                            )
                    )}
                </Section>

                <Section data-test="mobile-menu-legal-links">
                    <SectionHeader>
                        {`© ${new Date().getFullYear()} Zocdoc, Inc.`}
                    </SectionHeader>
                    {this.renderFooterLink(
                        `${hostOverride}/about/terms/`,
                        'Terms',
                        'LegalLinks',
                        'Terms'
                    )}
                    {this.renderFooterLink(
                        `${hostOverride}/about/privacypolicy/`,
                        'Privacy',
                        'LegalLinks',
                        'Privacy'
                    )}
                    {this.renderFooterLink(
                        `${hostOverride}/privacy-settings`,
                        'DoNotSellMyPersonalInformation',
                        'LegalLinks',
                        'Do not sell my personal information'
                    )}
                    <LinkFarmToggle
                        toggleText="Site map"
                        toggleState={this.state.viewSitemap}
                        onToggleClick={this.handleSitemapToggleClick}
                    />
                    <Sitemap
                        isVisible={this.state.viewSitemap}
                        hostOverride={hostOverride}
                    />
                </Section>
            </Footer>
        );
    }

    toggleMenu = () => this.setState((state) => ({ open: !state.open }));

    handleWindowResize = () => {
        if (window.innerWidth >= Breakpoints.medium && this.state.open) {
            this.toggleMenu();
        }
    };

    handleSitemapToggleClick = () => {
        this.setState((previousState) => ({
            viewSitemap: !previousState.viewSitemap,
        }));
    };
    handleMoreSpecialtiesToggleClick = () => {
        this.setState((previousState) => ({
            viewMoreSpecialties: !previousState.viewMoreSpecialties,
        }));
    };
    handleMoreInsurancesToggleClick = () => {
        this.setState((previousState) => ({
            viewMoreInsurances: !previousState.viewMoreInsurances,
        }));
    };
    handleTopSpecialtiesToggleClick = (specialtyName) => {
        this.setState((previousState) => ({
            showTopSpecialties: {
                ...previousState.showTopSpecialties,
                [specialtyName]:
                    !previousState.showTopSpecialties[specialtyName],
            },
        }));
    };
}

export default withTheme(MobileMenu);

const MenuTriggerArea = styled.div`
    background: none;
    border: 0;
    color: inherit;
    cursor: pointer;
    position: relative;
    margin: 0 20px 0 0;
    padding: 0;
    ${breakpoint.medium} {
        display: none;
    }
`;

const MenuTrigger = styled.button`
    color: transparent;
    background-color: transparent;
    position: absolute;
    border: none;
    top: -22px;
    left: -18px;
    height: 54px;
    width: 50px;

    ${breakpoint.small} {
        left: -36px;
        width: 86px;
    }
`;

const Menu = styled.div`
    align-items: center;
    background-color: ${white};
    display: flex;
    flex-direction: column;
    overflow: auto;
    overflow-x: hidden;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 99;
    transform: translate3d(0, 0, 0);

    -webkit-overflow-scrolling: touch;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    > * {
        flex-shrink: 0;
    }
`;

const StyledCloseContainer = styled.div`
    box-sizing: content-box;
    cursor: pointer;
    position: absolute;
    padding: 19px 0 20px 0;
    width: 46px;
    top: 0;
    left: 0;
    text-align: center;
`;

const StyledClose = styled(Close)`
    font-size: 16px;
`;

const Home = styled.div`
    align-items: center;
    background-color: ${(p) =>
        p.isWhiteLabel ? p.theme.header.mobileMenu.home.bgColor : { white }};
    color: ${(p) =>
        p.isWhiteLabel ? p.theme.header.mobileMenu.home.textColor : { navy }};
    display: flex;
    font: 20px/1.3 ${FontFamily.SharpSansSemibold};
    justify-content: center;
    position: relative;
    height: ${(p) => (p.isWhiteLabel ? '96px' : '55px')};
    width: 100%;
`;

const HomeLink = styled(ZdLink)`
    border-bottom-color: ${(p) => p.theme.header.mobileMenu.home.borderColor};
    color: inherit;
    display: inline-block;
    letter-spacing: 0.1px;
    margin-left: 10px;
`;

const SignIn = styled.div`
    align-items: center;
    background-color: ${green};
    color: ${navy};
    display: flex;
    font: 18px/1.3 ${FontFamily.SharpSansBold};
    justify-content: space-evenly;
    height: 84px;
    width: 100%;
`;

const SignInLink = styled(ZdLink)`
    width: 50%;
    text-align: center;
    border: none;
    color: ${navy};
`;

const StyledMfaWrapper = styled.div`
    margin-bottom: 20px;
`;

const StyledEmailFirstSignInFormView = styled(EmailFirstSignInFormView)`
    margin-bottom: 20px;
`;

const UserType = styled.div`
    display: block;
    font: 16px/18px ${FontFamily.SharpSansBold};
`;

const Divider = styled.div`
    border-left: 1px solid ${navy};
    height: 50px;
`;

const StackedSignIn = styled.div`
    width: 100%;
`;

const SignInText = styled.div`
    background-color: ${(p) => (p.isGreen ? green : yellow)};
    color: ${navy};
    font: 15px ${FontFamily.SharpSansSemibold};
    line-height: 18px;
    text-align: center;
    padding: 11px 0;
    margin: 0 16px;

    ${(p) => (p.showTopBorder ? `border-top: 1px solid ${grey25};` : '')}
`;

const CollapsibleLogin = styled(CollapsibleSection)`
    ${CollapsibleSectionPrimitives.Header} {
        background-color: ${white};
        color: ${navy};
        font: 15px ${FontFamily.SharpSansSemibold};
        text-align: center;
        display: block;
        height: 52px;
    }

    ${CollapsibleSectionPrimitives.Children} {
        &::before {
            content: none;
        }
        padding: 7px 16px 8px 16px;
    }

    ${CollapsibleSectionPrimitives.StyledArrow} {
        margin-left: 4px;
        margin-bottom: 2px;
        height: 5px;
        width: 8px;
    }
`;

const AccountText = styled.div`
    color: inherit;
    display: inline-block;
    letter-spacing: 0.1px;
    font: 14px/15px ${FontFamily.SharpSansSemibold};
    border-bottom: 1px dotted;
`;

const Footer = styled.div`
    width: 100%;
    background-color: ${navy};
    padding-top: 12px;
    display: flex;
    flex-direction: column;
`;

const Section = styled.div`
    display: flex;
    flex-direction: ${(p) => (p.horizontal ? 'row' : 'column')};
    padding-bottom: 16px;
`;

const SectionHeader = styled.div`
    color: ${white};
    display: inline-block;
    padding: 0 16px;
    font: 15px/2.6 ${FontFamily.SharpSansBold};
`;

const CustomLinkSection = styled(Section)`
    margin: 60px 0;
`;

const Link = styled(ZdLink)`
    display: inline-block;
    margin: 8px 0;
`;

const FooterLink = styled(ZdLink)`
    display: ${(p) => (p.isVisible ? 'inline-block' : 'none')};
    padding: 0 0 0 16px;
    color: rgba(255, 255, 255, 0.6);
    &:visited : rgba(255, 255, 255, 1.0);
    border: 0;
    font: 15px/2.6 ${FontFamily.SharpSansSemibold};
`;

const StyledPoweredByZocdoc = styled(PoweredByZocdoc)`
    color: #00234b;
    font-size: 11px;
`;

const UserSection = styled.div`
    width: 100%;
`;

const UserLinkContainer = styled.div`
    padding: 0 0 9px 0;
`;

const UserLink = styled(ZdLink)`
    color: ${navy};
    display: flex;
    border-bottom: none !important;
    align-items: center;
    justify-content: space-between;
    padding: 10px 0;
`;

const UserLinkText = styled.div`
    margin: 0 0 0 2px;
    &:first-child {
        margin-left: 16px;
    }
    font: 15px/20px ${FontFamily.SharpSansSemibold};
    flex-grow: 1;
`;

const UserLinkIcon = styled.div`
    align-items: center;
    display: flex;
    height: 20px;
    justify-content: center;
    line-height: 0px;
    margin: 0;
    width: 46px;
`;
